import { useState, useEffect } from 'react';
import { localStorageService } from '@/utils/local_storage';
import { deleteCookie, setCookie, getCookie } from '@/utils/cookies';
import omit from 'lodash/omit';

/**
 * Stores session-related data that needs to persist beyond JWT session expiry.
 * The provider is set server-side as a cookie during SAML login and synced into
 * localStorage here so it remains accessible client-side after session cookies expire.
 *
 * ex. SAML sessions need the "provider" to persist so that when the session
 * times out we can show the SAML login modal instead of the generic one.
 */

const STORAGE_KEY = 'one-degree:' + (typeof window !== 'undefined' ? window.location?.host : 'server') + ':session';

export const useLocalStorageSession = () => {
  const [sessionObject, setSessionObject] = useState({});

  // Read from localStorage on mount (client-only).
  useEffect(() => {
    setSessionObject(localStorageService.get(STORAGE_KEY) || {});
  }, []);

  // it's possible for the server to set the provider cookie and localStorage session won't have that value
  // on initial run, check if provider is present to keep localStorage in sync with cookie.
  useEffect(() => {
    let provider = getCookie('provider');
    if (provider) {
      // already in cookies, no need to overwrite
      setSessionValue('provider', provider, false);
    }
  }, []);

  const setSessionValue = (key, value, saveAsCookie = true, cookieKey = false) => {
    let updatedSessionObject;
    if (typeof value === 'undefined') {
      updatedSessionObject = { ...omit(sessionObject, key) };
      setSessionObject(updatedSessionObject);
      if (saveAsCookie) {
        deleteCookie(cookieKey || key);
      }
    } else {
      if (saveAsCookie) {
        setCookie(cookieKey || key, value);
      }
      updatedSessionObject = { ...sessionObject };
      updatedSessionObject[key] = value;
      setSessionObject(updatedSessionObject);
    }
    return localStorageService.set(STORAGE_KEY, updatedSessionObject);
  };

  const deleteSessionValue = (key, saveAsCookie = true, cookieKey = false) => {
    return setSessionValue(key, 'undefined', saveAsCookie, cookieKey);
  };

  const clearLocalStorageSession = () => {
    localStorageService.remove(STORAGE_KEY);
  };

  return {
    clearLocalStorageSession,
    deleteSessionValue,
    sessionObject,
    setSessionValue
  };
};

export default useLocalStorageSession;
